import { useEffect, useState } from 'react'
import { animate, motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { useReducedMotion } from '@/components/motion'

const API_BASE = 'https://reeva-api.returned.com'

interface BetaSignupProps {
  source?: string
}

export function BetaSignup({ source = 'returned-home' }: BetaSignupProps) {
  const prefersReducedMotion = useReducedMotion()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')
  const [position, setPosition] = useState<number | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !email.includes('@')) {
      setStatus('error')
      setMessage('Please enter a valid email address')
      return
    }

    setStatus('loading')
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 10_000)
    try {
      const res = await fetch(`${API_BASE}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source }),
        signal: controller.signal,
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.success) {
        setStatus('success')
        setMessage(data.message || 'You are on the list!')
        setPosition(data.position ?? null)
      } else {
        setStatus('error')
        setMessage(data.message || 'Something went wrong')
      }
    } catch {
      setStatus('error')
      setMessage('Failed to sign up. Please try again.')
    } finally {
      clearTimeout(timeout)
    }
  }

  if (status === 'success') {
    const drawn = prefersReducedMotion ? 1 : undefined
    return (
      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="beta-signup-success"
      >
        <motion.svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <motion.circle cx="12" cy="12" r="10" initial={{ pathLength: drawn ?? 0 }} animate={{ pathLength: 1 }} transition={{ duration: drawn ? 0 : 0.4, ease: 'easeOut' }} />
          <motion.path d="M8 12.5l2.6 2.6L16 9.5" initial={{ pathLength: drawn ?? 0 }} animate={{ pathLength: 1 }} transition={{ duration: drawn ? 0 : 0.3, delay: drawn ? 0 : 0.35, ease: 'easeOut' }} />
        </motion.svg>
        <span>{message}</span>
        {typeof position === 'number' && position > 0 && <PositionCount position={position} />}
      </motion.div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="beta-signup-form">
      <input
        type="email"
        name="email"
        autoComplete="email"
        placeholder="you@email.com"
        value={email}
        onChange={(e) => { setEmail(e.target.value); setStatus('idle') }}
        className="beta-signup-input"
        disabled={status === 'loading'}
      />
      <button
        type="submit"
        className="beta-signup-button"
        disabled={status === 'loading'}
      >
        {status === 'loading' ? <Loader2 size={16} className="spin" /> : 'Join private beta'}
      </button>
      {status === 'error' && <p className="beta-signup-error">{message}</p>}
    </form>
  )
}

function PositionCount({ position }: { position: number }) {
  const prefersReducedMotion = useReducedMotion()
  const [shown, setShown] = useState(prefersReducedMotion || position <= 1 ? position : 0)
  useEffect(() => {
    if (prefersReducedMotion || position <= 1) return
    const controls = animate(0, position, {
      duration: 0.7,
      ease: 'easeOut',
      onUpdate: (v) => setShown(Math.round(v)),
    })
    return () => controls.stop()
  }, [position, prefersReducedMotion])
  return (
    <span className="beta-signup-position">
      <span aria-hidden="true">#{shown} in line</span>
      <span className="sr-only">{`number ${position} in line`}</span>
    </span>
  )
}
